import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';

const ROLE_LABELS = {
  admin: { label: 'Admin', color: 'text-error-400 bg-error-900/30' },
  operator: { label: 'Operator', color: 'text-warning-400 bg-warning-900/30' },
  editor: { label: 'Editor', color: 'text-blue-400 bg-blue-900/30' },
  dj: { label: 'DJ', color: 'text-secondary-400 bg-secondary-900/30' },
  advertising: { label: 'Advertising', color: 'text-purple-400 bg-purple-900/30' },
};

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get('/api/users', { params });
      setUsers(data);
    } catch (err) {
      console.error('[UserList] Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const { data } = await api.put(`/api/users/${user.id}`, {
        is_active: !user.is_active,
      });
      setUsers(users.map((u) => (u.id === user.id ? { ...u, ...data } : u)));
    } catch (err) {
      console.error('[UserList] Toggle failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/users/${confirmItem.id}`);
      setUsers(users.filter((u) => u.id !== confirmItem.id));
    } catch (err) {
      console.error('[UserList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  const getRoleBadge = (role) => {
    const config = ROLE_LABELS[role] || { label: role, color: 'text-dark-400 bg-dark-700' };
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Users</h1>
        <Link to="/users/create" className="btn btn-primary btn-sm">
          + New User
        </Link>
      </div>

      <input
        type="text"
        placeholder="Search users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="input max-w-sm"
      />

      {users.length === 0 ? (
        <div className="py-12 text-center text-dark-400">No users found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="font-medium text-dark-100">
                    <Link to={`/users/${user.id}/edit`} className="hover:text-primary-400">
                      {user.name}
                    </Link>
                  </td>
                  <td className="text-dark-300">{user.email}</td>
                  <td>{getRoleBadge(user.role)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {user.is_active ? (
                        <span className="text-xs text-success-400">● Active</span>
                      ) : (
                        <span className="text-xs text-dark-500">○ Inactive</span>
                      )}
                      <button
                        onClick={() => handleToggleActive(user)}
                        className="text-xs text-dark-500 hover:text-dark-300"
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? '●' : '○'}
                      </button>
                    </div>
                  </td>
                  <td className="text-sm text-dark-400">
                    {user.last_login ? new Date(user.last_login).toLocaleString() : '—'}
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/users/${user.id}/edit`}
                      className="text-xs text-dark-400 hover:text-dark-200 mr-1"
                      title="Edit"
                    >
                      ✎
                    </Link>
                    <button
                      onClick={() => {
                        setConfirmItem(user);
                        setConfirmOpen(true);
                      }}
                      className="text-xs text-error-400 hover:text-error-300"
                      title="Delete"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete User"
        message={`Are you sure you want to delete "${confirmItem?.name}"? This action cannot be undone.`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
